import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6 } from "styled-components";
import { Card, type InfoCardsCardViewProps } from "~/features/blocks/inventory/info-cards/card";
import { takeFirst } from "~/features/app/utils";
import { Wrap } from "~/features/ui/wrap";
type SectionDataAttrs = Record<`data-${string}`, string | undefined>;
export type InfoCardsBlockViewProps<TCard = Omit<InfoCardsCardViewProps, "ratio">> = {
  anchor?: Array<{
    id?: string | null;
    name?: string | null;
  }> | {
    id?: string | null;
    name?: string | null;
  } | null;
  cards?: TCard[] | null;
  getCardKey?: (card: TCard, index: number) => React.Key;
  hasTopDivider?: boolean | null;
  headline?: string | null;
  isHighlighted?: boolean | null;
  mode?: string | null;
  renderCard: (card: TCard, index: number, ratio: "3/2" | "16/9") => React.ReactNode;
  sectionProps?: React.ComponentPropsWithoutRef<"section"> & SectionDataAttrs;
  theme?: string | null;
};
export function InfoCardsBlockView<TCard = Omit<InfoCardsCardViewProps, "ratio">>({
  anchor,
  cards = [],
  getCardKey,
  hasTopDivider = true,
  headline,
  isHighlighted = false,
  mode = "light",
  renderCard,
  sectionProps,
  theme = "white"
}: InfoCardsBlockViewProps<TCard>) {
  const entries = cards ?? [];
  return <_StyledSection {...sectionProps} data-theme={mode} id={takeFirst(anchor)?.id || undefined} data-anchor-name={takeFirst(anchor)?.name || undefined} $_css={[{
    "position": "relative",
    "backgroundColor": "var(--bg-secondary)",
    "paddingBottom": "var(--spacing-xl)",
    "paddingTop": "var(--spacing-3xl)",
    "@media (min-width: 1024px)": {
      "paddingBottom": "var(--spacing-2xl)",
      "paddingTop": "var(--spacing-4xl)"
    }
  }, hasTopDivider && {
    "borderTopWidth": "1px",
    "borderColor": "var(--stroke-primary)"
  }, theme === "grey" && {
    "backgroundColor": "var(--bg-tertiary)"
  }, isHighlighted && {
    "backgroundColor": "var(--white-static)"
  }]}>
      <_StyledWrap>
        <_StyledDiv>
          {headline && <_StyledH>
              {headline}
            </_StyledH>}
          <_StyledDiv2>
            {entries.map((card, idx) => <_StyledDiv3 key={getCardKey?.(card, idx) || idx}>
                {renderCard(card, idx, entries.length === 3 ? "3/2" : "16/9")}
              </_StyledDiv3>)}
          </_StyledDiv2>
        </_StyledDiv>
      </_StyledWrap>
    </_StyledSection>;
}
var _StyledSection = /*#__PURE__*/_styled("section").withConfig({
  componentId: "sc-sf-f5iqog-0"
})`${p => p.$_css}`;
var _StyledWrap = _styled2(Wrap).withConfig({
  componentId: "sc-sf-f5iqog-1"
})({
  "borderWidth": "0px"
});
var _StyledDiv = _styled3("div").withConfig({
  componentId: "sc-sf-f5iqog-2"
})({
  "paddingLeft": "var(--spacing-md)",
  "paddingRight": "var(--spacing-md)",
  "@media (min-width: 1024px)": {
    "paddingLeft": "var(--spacing-xl)",
    "paddingRight": "var(--spacing-xl)"
  }
});
var _StyledH = _styled4("h2").withConfig({
  componentId: "sc-sf-f5iqog-3"
})({
  "paddingLeft": "var(--spacing-md)",
  "paddingRight": "var(--spacing-md)",
  "fontSize": "2.8rem",
  "lineHeight": "1.2",
  "fontWeight": "400",
  "color": "var(--text-primary)",
  "@media (min-width: 1024px)": {
    "paddingLeft": "var(--spacing-xl)",
    "paddingRight": "var(--spacing-xl)",
    "fontSize": "4.4rem",
    "lineHeight": "1.3",
    "letterSpacing": "0.044rem",
    "fontWeight": "400"
  }
});
var _StyledDiv2 = _styled5("div").withConfig({
  componentId: "sc-sf-f5iqog-4"
})({
  "marginTop": "var(--spacing-xl)",
  "display": "flex",
  "flexDirection": "column",
  "gap": "var(--spacing-sm)",
  "@media (min-width: 1024px)": {
    "marginTop": "var(--spacing-2xl)",
    "flexDirection": "row",
    "gap": "var(--spacing-md)"
  }
});
var _StyledDiv3 = /*#__PURE__*/_styled6("div").withConfig({
  displayName: "info-cards___StyledDiv3",
  componentId: "sc-11a0mxd-0"
})({
  "flex": "1 1 0%"
});